import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, Trash2, AlertTriangle, Clock, ShieldCheck } from 'lucide-react';

export default function DeleteAccountPage() {
  const [copied, setCopied] = useState(false);
  const supportEmail = 'admin@christkingdomglobalmarketplace.com';

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('Clipboard write failed:', err?.message || err);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main
        className="flex-1 max-w-3xl mx-auto px-6 md:px-12 py-12"
        data-testid="delete-account-page"
      >
        {/* Page Heading */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-semibold">
            Delete Your Account
          </h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Christ Kingdom Global Marketplace — operated by Datasam LLC
        </p>

        {/* Intro */}
        <section className="mb-8">
          <p className="text-base leading-relaxed">
            You can permanently delete your Christ Kingdom Global Marketplace account and the
            personal data associated with it at any time. This page explains exactly what
            happens when you request deletion, how long it takes, and what (if anything) we
            are legally required to retain.
          </p>
        </section>

        {/* Step-by-step */}
        <section className="mb-8">
          <h2 className="text-xl font-heading font-semibold mb-4">
            How to Request Account Deletion
          </h2>

          <ol className="space-y-5">
            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white font-semibold flex items-center justify-center">
                1
              </span>
              <div>
                <p className="font-medium mb-1">Email us from your registered address</p>
                <p className="text-sm text-muted-foreground mb-2">
                  Send an email from the email address linked to your Christ Kingdom account
                  to:
                </p>
                <button
                  type="button"
                  onClick={copyEmail}
                  className="inline-flex items-center gap-2 bg-muted hover:bg-muted/70 transition-colors px-4 py-2 rounded-lg text-sm font-medium border border-border"
                  data-testid="copy-support-email"
                >
                  <Mail className="w-4 h-4" />
                  {supportEmail}
                  <span className="text-xs text-muted-foreground ml-2">
                    {copied ? 'Copied!' : 'click to copy'}
                  </span>
                </button>
              </div>
            </li>

            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white font-semibold flex items-center justify-center">
                2
              </span>
              <div>
                <p className="font-medium mb-1">Use this subject line</p>
                <code className="block bg-muted/50 rounded px-3 py-2 text-sm font-mono">
                  Delete my account — [your account email]
                </code>
              </div>
            </li>

            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white font-semibold flex items-center justify-center">
                3
              </span>
              <div>
                <p className="font-medium mb-1">We confirm and process within 7 days</p>
                <p className="text-sm text-muted-foreground">
                  Our team will reply within 24 hours to verify your identity, then permanently
                  delete your account and personal data within 7 business days. You will
                  receive a final confirmation email when deletion is complete.
                </p>
              </div>
            </li>
          </ol>
        </section>

        {/* What gets deleted */}
        <section className="mb-8">
          <h2 className="text-xl font-heading font-semibold mb-4">
            What Data Will Be Deleted
          </h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">✓</span>
                <span>Your name, email address, and password</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">✓</span>
                <span>Your shipping addresses and phone numbers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">✓</span>
                <span>Your saved cart, wishlist, and product reviews</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">✓</span>
                <span>
                  Your vendor profile, store listings, and uploaded product photos (if you
                  are a vendor)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">✓</span>
                <span>Your KYC documents (Business License, Government ID)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">✓</span>
                <span>
                  All linked Google / OAuth identifiers (if you signed up with Google)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">✓</span>
                <span>Push notification subscriptions and PWA analytics tied to your account</span>
              </li>
            </ul>
          </div>
        </section>

        {/* What we must keep */}
        <section className="mb-8">
          <h2 className="text-xl font-heading font-semibold mb-4">
            What We Are Legally Required to Retain
          </h2>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                Certain records must be kept by law and cannot be deleted on request:
              </p>
            </div>
            <ul className="space-y-2 text-sm ml-8">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold mt-0.5">•</span>
                <span>
                  <strong>Completed transaction records</strong> — kept for{' '}
                  <strong>7 years</strong> for tax, anti-money-laundering (AML), and accounting
                  compliance (US IRS, EU VAT, state sales-tax laws)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold mt-0.5">•</span>
                <span>
                  <strong>Payment processor records</strong> — Stripe retains transaction
                  metadata independently under their own retention policy (typically 7 years).
                  We do not control Stripe's records.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold mt-0.5">•</span>
                <span>
                  <strong>Fraud / abuse records</strong> — accounts flagged for fraud, chargebacks,
                  or policy violations may have a minimal record retained to prevent re-registration.
                </span>
              </li>
            </ul>
            <p className="text-xs text-amber-800 mt-4">
              These records are anonymized to the extent legally possible and are not used for any
              purpose other than legal compliance.
            </p>
          </div>
        </section>

        {/* Timing */}
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-heading font-semibold">How Long It Takes</h2>
          </div>
          <ul className="text-sm space-y-1 ml-8">
            <li>• Initial confirmation reply: within <strong>24 hours</strong></li>
            <li>• Account deletion completed: within <strong>7 business days</strong></li>
            <li>• Backup purge (final removal from disaster-recovery snapshots): within <strong>30 days</strong></li>
          </ul>
        </section>

        {/* Security */}
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-heading font-semibold">Verification & Security</h2>
          </div>
          <p className="text-sm leading-relaxed">
            To protect your account from unauthorized deletion, we may ask you to verify your
            identity before processing the request. This may include confirming a recent order
            ID, the country you registered from, or replying from an email address linked to
            your account.
          </p>
        </section>

        {/* Related */}
        <section className="border-t border-border pt-6 text-sm text-muted-foreground">
          <p>
            Related: <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
            {' • '}
            <a href="/terms" className="text-primary hover:underline">Terms of Service</a>
            {' • '}
            <a href="/refund-policy" className="text-primary hover:underline">Refund Policy</a>
          </p>
          <p className="mt-2 text-xs">Last updated: May 20, 2026</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
